import { SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY } from "../config/supabase-public"
import { apiUrl } from "../config/api"
import project from "../../project.json"
import { templateById } from "../data/site-templates"

/**
 * A published customer site, read from Storage — FCS-057.
 * ============================================================================
 * Every customer subdomain serves the SAME build. There is no per-customer
 * bundle and no per-customer deploy: the page loads, reads the subdomain out
 * of its own address, fetches that site's JSON, and renders the template the
 * JSON names. One frontend, many websites.
 *
 * ── THE JSON IS THE SITE, AND IT IS AUTHORITATIVE ──────────────────────────
 * FCS-057(b) is explicit and it is the right rule: in remote mode there is no
 * falling back to the template's built-in demo content. A visitor who lands on
 * a contractor's address must see that contractor's website or a page saying
 * the site is unavailable — never a stranger's sample text under their
 * domain, which is worse than an error because it looks deliberate.
 *
 * So `load` below returns either a validated site or a reason, and the reason
 * is what gets rendered.
 */

const BUCKET = project.sites?.bucket || "site-content"

/** The shape this build knows how to read. A newer file is refused, loudly. */
export const SCHEMA_VERSION = 1

/* Hosts that are the product itself rather than somebody's website. `www` is
   in here because www.freecontractorservices.com is the marketing site, not a
   customer called "www". */
const RESERVED = ["www", "app", "admin", "api", "sb", "mail", "static", "assets"]

/**
 * The customer subdomain in a hostname, or "" when this is the main site.
 *
 * Deliberately narrow: exactly one label in front of the apex. A deeper host
 * is not a customer site, and treating it as one would have this page try to
 * render something for every stray DNS entry that ever points here.
 */
export function subdomainFromHost(hostname) {
  const host = String(hostname || "").toLowerCase().replace(/:\d+$/, "")
  if (!host || host === "localhost" || /^\d+(\.\d+){3}$/.test(host)) return ""

  const parts = host.split(".")
  // apex.tld is two labels; a customer adds exactly one in front.
  if (parts.length !== 3) return ""

  const [sub] = parts
  if (!sub || RESERVED.includes(sub)) return ""
  if (!/^[a-z0-9]+(-[a-z0-9]+)*$/.test(sub)) return ""
  return sub
}

/**
 * Is this a host that is neither ours nor a development address? — FCS-117.
 * Only such a host can be a customer's own domain; for everything else there is
 * nothing to look up, so a visitor to our own site never pays for a lookup.
 */
export function isForeignHost(hostname) {
  const host = String(hostname || "").toLowerCase().replace(/:\d+$/, "")
  if (!host || host === "localhost" || host.endsWith(".localhost") || /^\d+(\.\d+){3}$/.test(host)) return false
  const apex = new URL(project.site?.url || "https://freecontractorservices.com").hostname
  if (host === apex || host.endsWith(`.${apex}`)) return false
  return host.includes(".")
}

/**
 * Which published site this address shows, or "".
 *
 * name.freecontractorservices.com carries the answer in the address. A customer's
 * own domain does not, so it is looked up (resolve-host: connected, active, and
 * whose live site is which). Never throws: an address nobody can place is
 * simply "", and the page falls back to ours.
 */
const asked = {}
export function resolveSite(hostname) {
  const named = subdomainFromHost(hostname)
  if (named) return Promise.resolve(named)
  if (!isForeignHost(hostname)) return Promise.resolve("")
  // One lookup per address per page load, however many components ask.
  if (!asked[hostname]) asked[hostname] = lookUp(hostname)
  return asked[hostname]
}

async function lookUp(hostname) {
  try {
    const res = await fetch(apiUrl("resolve-host"), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        apikey: SUPABASE_PUBLISHABLE_KEY,
        Authorization: `Bearer ${SUPABASE_PUBLISHABLE_KEY}`,
      },
      body: JSON.stringify({ host: hostname }),
    })
    const body = await res.json().catch(() => ({}))
    return typeof body.subdomain === "string" ? body.subdomain : ""
  } catch {
    return ""
  }
}

/** Where a published site's JSON lives. Public: no key, no token. */
export function contentUrl(subdomain) {
  return `${SUPABASE_URL}/storage/v1/object/public/${BUCKET}/content/${subdomain}.json`
}

/**
 * What a site must have before it is allowed to render.
 *
 * Checked rather than assumed, because the failure this prevents is silent:
 * a template reading an absent field draws an empty heading and a phone
 * button that dials nothing, and the contractor finds out from a customer.
 */
function validate(file, subdomain) {
  if (!file || typeof file !== "object") return "This site's settings could not be read."

  if (file.schema_version !== SCHEMA_VERSION) {
    return `This site was published in a format this page does not know how to read (v${file.schema_version}).`
  }
  if (file.subdomain && file.subdomain !== subdomain) {
    return "This site's settings belong to a different address."
  }

  const template = templateById(file.template_id)
  if (!template || template.id !== file.template_id) {
    return `This site asks for a design that does not exist here (${file.template_id}).`
  }

  const content = file.content
  if (!content || typeof content !== "object") return "This site has no content."
  if (!content.business || !String(content.business.name || "").trim()) {
    return "This site has no business name."
  }

  return ""
}

/**
 * The draft shape, guaranteed — FCS-057(b).
 *
 * The templates read `draft.services.filter(…)` and half a dozen collections
 * like it, because inside the builder every one of them always exists. A
 * published file is written by a different program on a different day, and one
 * absent array is a blank page with a stack trace behind it.
 *
 * This is NOT falling back to demo content, which remote mode forbids and
 * which would be far worse: every default here is EMPTY. A site that named no
 * services renders with no services section, which is true. What the file
 * actually said is never replaced — see `validate` for the fields that are
 * required, and which stop the render rather than being filled in.
 */
function normalise(content) {
  return {
    ...content,
    business: {
      name: "",
      tagline: "",
      phone: "",
      email: "",
      about: "",
      ...(content.business || {}),
    },
    services: Array.isArray(content.services) ? content.services : [],
    serviceCopy: content.serviceCopy || {},
    images: content.images || {},
    gallery: Array.isArray(content.gallery) ? content.gallery : [],
    reviews: Array.isArray(content.reviews) ? content.reviews : [],
    faqs: Array.isArray(content.faqs) ? content.faqs : [],
    hours: Array.isArray(content.hours) ? content.hours : [],
    areas: {
      states: [],
      wholeStates: [],
      counties: [],
      countyNames: [],
      focus: "",
      ...(content.areas || {}),
    },
    extras: content.extras || {},
    benefits: content.benefits || {},
  }
}

/**
 * Loads a published site.
 *
 * Returns `{ ok: true, site }` or `{ ok: false, reason }`. Never throws and
 * never returns half a site: the caller has exactly two cases to render.
 */
export async function load(subdomain) {
  if (!subdomain) return { ok: false, reason: "There is no website at this address." }

  let res
  try {
    res = await fetch(contentUrl(subdomain), { cache: "no-cache" })
  } catch {
    return { ok: false, reason: "This website could not be reached just now." }
  }

  if (res.status === 404 || res.status === 400) {
    return { ok: false, reason: "There is no website at this address yet." }
  }
  if (!res.ok) return { ok: false, reason: "This website could not be loaded just now." }

  let file
  try {
    file = await res.json()
  } catch {
    return { ok: false, reason: "This site's settings could not be read." }
  }

  const problem = validate(file, subdomain)
  if (problem) return { ok: false, reason: problem }

  return { ok: true, site: { ...file, content: normalise(file.content) } }
}
